import { DOCUMENT } from '@angular/common';
import { Injectable, Injector, effect, inject, untracked, type EffectRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs';

import { ANALYTICS_EVENTS } from '../analytics/events';
import { AnalyticsService } from '../services/analytics.service';
import { FeatureFlagsViewerService } from '../services/feature-flags-viewer.service';
import { AppActionRegistryService } from './app-action-registry.service';
import { askParamFrom, sanitiseAskPrompt, type SanitisedAskPrompt } from './ask-prompt';

/**
 * RESPONSIBILITY: the `?ask=<prompt>` deep link — `/#/home?ask=Find%20a%20room`
 * opens the Ask panel with that question in the composer.
 *
 * Handled ONCE, at startup (`start()`, called by the shell's constructor):
 *   1. read `ask` from the hash route's query (or the page search string);
 *   2. clean it (`sanitiseAskPrompt`: controls and bidi overrides out, markup
 *      out, whitespace folded, capped at 500 characters);
 *   3. take it out of the URL, so a refresh or a shared link does not carry it
 *      on and the address bar does not hold the viewer's question;
 *   4. wait for the viewer's flags, then PREFILL and focus the composer
 *      (`AppActionRegistryService.openAsk(…, 'prefill')`). It is never sent:
 *      a link is somebody else's words until the viewer presses Send.
 *
 * Ask off for the viewer: nothing opens, and `outcome: 'ai_unavailable'` says
 * so in analytics.
 *
 * SIGNALS: none.
 * USED BY: AppShellComponent.
 * BACKEND CONTRACT: none.
 * AI CONTEXT: the prompt text never reaches analytics — only its length.
 */
@Injectable({ providedIn: 'root' })
export class AskDeepLinkService {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly document = inject(DOCUMENT);
  private readonly flags = inject(FeatureFlagsViewerService);
  private readonly registry = inject(AppActionRegistryService);
  private readonly analytics = inject(AnalyticsService);
  private started = false;

  start(): void {
    if (this.started) return;
    this.started = true;
    const location = this.document.defaultView?.location;
    const fromHash = askParamFrom(location?.hash);
    const fromSearch = fromHash === null ? askParamFrom(location?.search) : null;
    const raw = fromHash ?? fromSearch;
    if (raw === null) return;
    this.stripFromUrl(fromHash !== null ? 'hash' : 'search');
    const prompt = sanitiseAskPrompt(raw);
    if (!prompt) return;
    this.deliverWhenReady(prompt);
  }

  private deliverWhenReady(prompt: SanitisedAskPrompt): void {
    let delivered = false;
    // The effect runs on a later tick, after `ref` is assigned.
    const ref: EffectRef = effect(() => {
      if (!this.flags.ready() || delivered) return;
      delivered = true;
      untracked(() => {
        const opened = this.registry.openAsk(prompt.text, 'prefill');
        this.analytics.capture(ANALYTICS_EVENTS.SHELL_ASK_DEEP_LINK_HANDLED, {
          outcome: opened ? 'prefilled' : 'ai_unavailable',
          chars: Array.from(prompt.text).length,
          truncated: prompt.truncated,
        });
      });
      ref.destroy();
    }, { injector: this.injector });
  }

  private stripFromUrl(where: 'hash' | 'search'): void {
    if (where === 'search') {
      const win = this.document.defaultView;
      if (!win) return;
      const url = new URL(win.location.href);
      url.searchParams.delete('ask');
      win.history.replaceState(win.history.state, '', url.toString());
      return;
    }
    const strip = (): void => {
      void this.router.navigate([], { queryParams: { ask: null }, queryParamsHandling: 'merge', replaceUrl: true });
    };
    // Before the first navigation lands, a navigation of our own would cancel
    // it — so wait for it.
    if (this.router.navigated) strip();
    else this.router.events.pipe(filter(e => e instanceof NavigationEnd), take(1)).subscribe(strip);
  }
}
